import { ChangeDetectionStrategy, Component, computed, inject, input, output } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FieldTree } from "@angular/forms/signals";
import { MediaType, StrategyDspCreativeType, StrategyDspFunnel } from "@front/m19-api-client";
import { DspCreativeAssetsService, dealsDurationRanges } from "@front/m19-services";
import { ModalService, ToasterService } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { take } from "rxjs";
import { DspFormService } from "@m19-board/dsp/dsp-create-strategy/dsp-form.service";
import { DspCreativeTypeSelectorComponent } from "@m19-board/dsp/dsp-creatives/dsp-creative-type-selector/dsp-creative-type-selector.component";
import { DspCreativeComponent } from "@m19-board/dsp/dsp-creatives/dsp-creative/dsp-creative.component";
import { UploadAmazonLoadingModalComponent } from "@m19-board/shared/media/upload-amazon-loading-modal/upload-amazon-loading-modal.component";

export interface DspCreateStrategyCreativeModel {
  creativeType: StrategyDspCreativeType | null;
  videoAssetId: string | null;
}

const VIDEO_CREATIVE_TYPES: StrategyDspCreativeType[] = [
  StrategyDspCreativeType.VIDEO,
  StrategyDspCreativeType.STREAMING_TV,
  StrategyDspCreativeType.VIDEO_DEAL,
];

@Component({
  selector: "app-dsp-create-strategy-creative-step",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, DspCreativeTypeSelectorComponent, DspCreativeComponent],
  templateUrl: "./dsp-create-strategy-creative-step.component.html",
})
export class DspCreateStrategyCreativeStepComponent {
  private readonly dspFormService = inject(DspFormService);
  private readonly dspCreativeAssetsService = inject(DspCreativeAssetsService);
  private readonly modalService = inject(ModalService);
  private readonly toasterService = inject(ToasterService);

  readonly creative = input.required<FieldTree<DspCreateStrategyCreativeModel>>();
  readonly funnel = input.required<StrategyDspFunnel | null>();
  readonly mediaCreativeType = input<StrategyDspCreativeType | null>(null);
  readonly dealId = input<string | null>(null);

  readonly videoDurationSeconds = output<number | undefined>();

  readonly MediaType = MediaType;

  private readonly accountMarketplace = toSignal(this.dspFormService.accountMarketplace$);
  private readonly videoDeals = toSignal(this.dspFormService.videoDeals$, { initialValue: [] });

  readonly isAwareness = computed(() => this.funnel() === StrategyDspFunnel.AWARENESS);

  private readonly effectiveCreativeType = computed(() =>
    this.isAwareness() ? this.mediaCreativeType() : this.creative().creativeType().value(),
  );

  readonly withVideoSection = computed(() => {
    if (this.isAwareness()) return true;
    const creativeType = this.effectiveCreativeType();
    return !!creativeType && VIDEO_CREATIVE_TYPES.includes(creativeType);
  });

  readonly isDisplayCreative = computed(
    () => this.creative().creativeType().value() === StrategyDspCreativeType.COMPONENT,
  );

  readonly videoDealAllowedDurations = computed(() => {
    if (this.effectiveCreativeType() !== StrategyDspCreativeType.VIDEO_DEAL) return [];
    const dealId = this.dealId();
    return dealsDurationRanges(this.videoDeals().filter((deal) => dealId === deal.dealId));
  });

  selectCreativeType(creativeType: StrategyDspCreativeType | undefined) {
    if (!creativeType) return;
    this.creative().creativeType().markAsDirty();
    this.creative().creativeType().value.set(creativeType);
    if (VIDEO_CREATIVE_TYPES.includes(creativeType)) {
      this.creative().videoAssetId().markAsDirty();
    }
  }

  selectVideo(videoAssetId: string) {
    this.creative().videoAssetId().markAsDirty();
    this.creative().videoAssetId().value.set(videoAssetId);
  }

  uploadVideo(file: File) {
    const accountMarketplace = this.accountMarketplace();
    if (!accountMarketplace?.dspAdvertiserId || !accountMarketplace.dspAdvertiser?.entityId) return;

    const ref = this.modalService.openModal(UploadAmazonLoadingModalComponent, {
      closeMode: "none",
    });
    this.dspCreativeAssetsService
      .uploadCreativeAsset(
        accountMarketplace.accountId,
        accountMarketplace.marketplace,
        accountMarketplace.dspAdvertiserId,
        file,
        MediaType.videoAsset,
        accountMarketplace.dspAdvertiser.entityId,
      )
      .pipe(take(1))
      .subscribe({
        next: (creativeAsset) => {
          ref.close();
          this.creative().videoAssetId().value.set(creativeAsset.assetId);
        },
        error: (err) => {
          ref.close();
          this.toasterService.error(err, "Video upload failed");
        },
      });
  }
}
